// Sun / moon button that switches the site between light and dark.
//
// <ThemeToggle /> sits in a page's top bar (marketing nav, business portal
// header, plan picker). Pages without a top bar get <FloatingThemeToggle />
// from the app layout, which hides itself while an inline toggle is visible,
// so every page shows exactly one.

import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import { mdiWeatherNight, mdiWeatherSunny } from "@mdi/js";

import useTheme from "./useTheme";
import "./theme-toggle.css";

const MOUNT_EVENT = "tabs-theme-toggle-mount";

// True when an inline toggle is on screen (some live in headers that are
// hidden at certain widths, e.g. the auth pages' mobile-only header).
function inlineToggleVisible() {
  return [...document.querySelectorAll(".theme-toggle--inline")].some((el) => el.getClientRects().length > 0);
}

function useInlineToggleVisible() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const sync = () => setVisible(inlineToggleVisible());
    window.addEventListener(MOUNT_EVENT, sync);
    window.addEventListener("resize", sync);
    sync();
    return () => {
      window.removeEventListener(MOUNT_EVENT, sync);
      window.removeEventListener("resize", sync);
    };
  }, []);
  return visible;
}

function ToggleButton({ className }) {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button
      type="button"
      className={className}
      onClick={toggle}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Light theme" : "Dark theme"}
      data-testid="theme-toggle"
    >
      <Icon path={dark ? mdiWeatherSunny : mdiWeatherNight} size={0.8} aria-hidden="true" />
    </button>
  );
}

export default function ThemeToggle({ className = "" }) {
  useEffect(() => {
    window.dispatchEvent(new Event(MOUNT_EVENT));
    return () => window.dispatchEvent(new Event(MOUNT_EVENT));
  }, []);
  return <ToggleButton className={`theme-toggle theme-toggle--inline ${className}`.trim()} />;
}

export function FloatingThemeToggle() {
  if (useInlineToggleVisible()) return null;
  return <ToggleButton className="theme-toggle theme-toggle--floating" />;
}
